import type { DashboardData, ExpenseInput } from "../types";

export type EntryOptions = DashboardData["entryOptions"];

/** Network/offline failures where a create should be kept locally and finished on sync. */
export function shouldDeferCreate(error: unknown): boolean {
  if (typeof navigator !== "undefined" && !navigator.onLine) return true;
  if (error instanceof TypeError) return true;
  if (typeof DOMException !== "undefined" && error instanceof DOMException && error.name === "AbortError")
    return true;
  if (error && typeof error === "object" && "status" in error) {
    const status = Number((error as { status?: number }).status);
    return status === 0 || status >= 500;
  }
  return false;
}

/**
 * Strip client-only flags and turn a locally deferred payee into payeeName for the server.
 * The server creates or matches the payee in the same atomic batch as the expense.
 */
export function prepareExpenseForSync(expense: ExpenseInput): ExpenseInput {
  const next: ExpenseInput = { ...expense };
  if (next.payeeId?.startsWith("local:")) {
    next.payeeName = next.payeeName || next.payeeId.slice("local:".length);
    delete next.payeeId;
  }
  if (next.payeeId) delete next.payeeName;
  delete next.newCategory;
  delete next.newSubcategory;
  return next;
}

function withCategory(
  options: EntryOptions,
  category: string,
  subcategory: string | undefined,
  isIncome: boolean,
): EntryOptions {
  const roots = isIncome ? options.incomeCategories : options.categories;
  const children = isIncome ? options.incomeSubcategoriesByCategory : options.subcategoriesByCategory;
  const nextRoots = roots.some((name) => name.toLowerCase() === category.toLowerCase())
    ? roots
    : [...roots, category].sort((a, b) => a.localeCompare(b));
  const parent = nextRoots.find((name) => name.toLowerCase() === category.toLowerCase()) || category;
  const existingChildren = children[parent] || children[category] || [];
  const nextChildren = !subcategory || existingChildren.some((name) => name.toLowerCase() === subcategory.toLowerCase())
    ? existingChildren
    : [...existingChildren, subcategory].sort((a, b) => a.localeCompare(b));
  const nextMap = { ...children, [parent]: nextChildren };
  return {
    ...options,
    categories: isIncome ? options.categories : nextRoots,
    subcategoriesByCategory: isIncome ? options.subcategoriesByCategory : nextMap,
    incomeCategories: isIncome ? nextRoots : options.incomeCategories,
    incomeSubcategoriesByCategory: isIncome ? nextMap : options.incomeSubcategoriesByCategory,
    subcategories: Object.values(isIncome ? options.subcategoriesByCategory : nextMap).flat(),
    incomeSubcategories: Object.values(isIncome ? nextMap : options.incomeSubcategoriesByCategory).flat(),
  };
}

/** Optimistic category lists while offline so the picker shows the new choice immediately. */
export function optionsWithLocalCategory(
  options: EntryOptions,
  category: string,
  subcategory: string | undefined,
  isIncome: boolean,
): EntryOptions {
  return withCategory(options, category, subcategory, isIncome);
}

type CategoryNeed = { category: string; subcategory?: string; isIncome: boolean };

/** Create any categories/subcategories marked on queued expenses before the expense POST. */
export async function ensureCategoriesForSync(
  needs: CategoryNeed[],
  request: typeof fetch = fetch,
): Promise<void> {
  if (!needs.length) return;
  const response = await request("/api/settings/categories", {
    method: "GET",
    credentials: "same-origin",
    signal: AbortSignal.timeout(45000),
  });
  const body = await response.json() as { entryOptions?: EntryOptions; error?: string };
  if (!response.ok || !body.entryOptions)
    throw new Error(body.error || "Could not load categories before sync.");
  let next = body.entryOptions;
  const expected = body.entryOptions;
  for (const need of needs) {
    if (!need.category || need.category === "Uncategorized") continue;
    next = withCategory(next, need.category, need.subcategory, need.isIncome);
  }
  if (JSON.stringify(next) === JSON.stringify(expected)) return;
  const save = await request("/api/settings/categories", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      categories: next.categories,
      subcategoriesByCategory: next.subcategoriesByCategory,
      incomeCategories: next.incomeCategories,
      incomeSubcategoriesByCategory: next.incomeSubcategoriesByCategory,
      expectedOptions: expected,
    }),
    signal: AbortSignal.timeout(45000),
  });
  const saved = await save.json() as { error?: string };
  if (!save.ok) throw new Error(saved.error || "Could not create categories before sync.");
}

/** Turn a deferred local payee into a real household payee id before loan/bill sync. */
export async function resolveDeferredPayee(
  payeeId: string | undefined,
  payeeName: string | undefined,
  request: typeof fetch = fetch,
): Promise<string> {
  if (payeeId && !payeeId.startsWith("local:")) return payeeId;
  const name = (payeeName || (payeeId?.startsWith("local:") ? payeeId.slice("local:".length) : "")).trim();
  if (!name) return "";
  const response = await request("/api/payees/save", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, type: "Person", notes: "" }),
    signal: AbortSignal.timeout(45000),
  });
  const body = await response.json() as { payee?: { id: string }; error?: string };
  if (!response.ok || !body.payee?.id)
    throw new Error(body.error || "Could not create the payee before sync.");
  return body.payee.id;
}

export function categoryNeedsFromExpense(expense: ExpenseInput): CategoryNeed[] {
  const isIncome = expense.type === "Income";
  const needs: CategoryNeed[] = [];
  if (expense.newCategory) needs.push({ category: expense.newCategory, isIncome });
  if (expense.newSubcategory && expense.category)
    needs.push({ category: expense.category, subcategory: expense.newSubcategory, isIncome });
  for (const part of expense.allocations || []) {
    if (part.category && part.category === expense.newCategory)
      needs.push({ category: part.category, subcategory: part.subcategory || undefined, isIncome });
  }
  return needs;
}
